import { default as React, useEffect, useMemo, useState } from 'react';
import {
  Content,
  Page,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { Entity } from '@backstage/catalog-model';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArticleIcon from '@mui/icons-material/Article';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';

const MCP_TYPE_ANNOTATION = 'skystage.wellsky.com/type';
const MCP_TYPE_VALUE = 'mcp-server';
const MCP_ENDPOINT_ANNOTATION = 'microapp/endpoint';
const MCP_TRANSPORT_ANNOTATION = 'microapp/transport';
const MCP_DOCS_ANNOTATION = 'microapp/docs';

const SERVER_COLORS = [
  '#4caf50',
  '#2196f3',
  '#ff9800',
  '#9c27b0',
  '#e91e63',
  '#00bcd4',
  '#ff5722',
  '#607d8b',
];

function getServerColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return SERVER_COLORS[Math.abs(hash) % SERVER_COLORS.length];
}

function getServerInitial(entity: Entity): string {
  return (entity.metadata.title || entity.metadata.name)
    .charAt(0)
    .toUpperCase();
}

function getMcpEndpoint(entity: Entity): string {
  return entity.metadata.annotations?.[MCP_ENDPOINT_ANNOTATION] ?? '';
}

function getMcpTransport(entity: Entity): string {
  return (
    entity.metadata.annotations?.[MCP_TRANSPORT_ANNOTATION] ?? 'streamable-http'
  );
}

function getMcpDocs(entity: Entity): string {
  return entity.metadata.annotations?.[MCP_DOCS_ANNOTATION] ?? '';
}

function getTeamName(entity: Entity): string {
  const owner = entity.spec?.owner as string | undefined;
  if (!owner) return '';
  return owner.split('/').pop()?.split(':').pop() || owner;
}

function getEntityUrl(entity: Entity): string {
  const namespace = entity.metadata.namespace || 'default';
  const kind = (entity.kind || 'component').toLowerCase();
  return `/catalog/${namespace}/${kind}/${entity.metadata.name}`;
}

function buildAddToClaudeCommand(entity: Entity): string {
  const endpoint = getMcpEndpoint(entity);
  const transport = getMcpTransport(entity);
  const transportFlag = transport === 'streamable-http' ? 'http' : transport;
  return `claude mcp add --transport ${transportFlag} --callback-port 9876 ${entity.metadata.name} ${endpoint}`;
}

const StepCard = ({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: React.ReactNode;
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2.5,
      height: '100%',
      borderRadius: 1,
      bgcolor: 'background.paper',
    }}
  >
    <Box display="flex" alignItems="flex-start" gap={2}>
      <Avatar
        sx={{
          bgcolor: '#e3f2fd',
          color: 'primary.main',
          width: 32,
          height: 32,
          fontSize: 14,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {step}
      </Avatar>
      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          {title}
        </Typography>
        {children}
      </Box>
    </Box>
  </Paper>
);

export const McpServersPage = () => {
  const catalogApi = useApi(catalogApiRef);
  const navigate = useNavigate();
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [filter, setFilter] = useState('');
  const [copySnackbar, setCopySnackbar] = useState(false);

  useEffect(() => {
    catalogApi
      .getEntities({
        filter: {
          [`metadata.annotations.${MCP_TYPE_ANNOTATION}`]: MCP_TYPE_VALUE,
        },
      })
      .then(response => {
        setEntities(response.items);
        setLoading(false);
      })
      .catch(err => {
        setError(err);
        setLoading(false);
      });
  }, [catalogApi]);

  const filteredEntities = useMemo(() => {
    if (!filter.trim()) return entities;
    const q = filter.toLowerCase();
    return entities.filter(
      e =>
        (e.metadata.title || e.metadata.name).toLowerCase().includes(q) ||
        (e.metadata.description ?? '').toLowerCase().includes(q),
    );
  }, [entities, filter]);

  const handleAddToClaude = (entity: Entity) => {
    const cmd = buildAddToClaudeCommand(entity);
    navigator.clipboard
      .writeText(cmd)
      .then(() => {
        setCopySnackbar(true);
      })
      .catch(() => {
        // clipboard access denied
      });
  };

  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;

  return (
    <Page themeId="tool">
      <Content noPadding>
        <Box
          sx={{
            bgcolor: 'grey.100',
            minHeight: '100%',
            p: 3,
          }}
        >
          {/* Intro section */}
          <Paper
            elevation={1}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 1,
            }}
          >
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Connect AI tools to WellSky services
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              mb={3}
              maxWidth={900}
            >
              MCP (Model Context Protocol) servers let Claude Code and other AI
              tools securely access internal data and services. All servers are
              protected by WellSky SSO — VPN is required, no credentials to
              manage.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 2,
              }}
            >
              <StepCard step={1} title="Browse available servers">
                <Typography variant="caption" color="text.secondary">
                  Find an MCP server below that provides the tools you need.
                </Typography>
              </StepCard>
              <StepCard step={2} title="Add to Claude Code">
                <Typography variant="caption" color="text.secondary">
                  Run{' '}
                  <Box
                    component="code"
                    sx={{
                      fontFamily: 'monospace',
                      bgcolor: 'grey.100',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontSize: '0.72rem',
                    }}
                  >
                    {
                      'claude mcp add --transport http --callback-port 9876 <name> <url>'
                    }
                  </Box>
                </Typography>
              </StepCard>
              <StepCard step={3} title="Authenticate once">
                <Typography variant="caption" color="text.secondary">
                  Sign in with WellSky SSO on first use. After that, it's
                  automatic.
                </Typography>
              </StepCard>
            </Box>
          </Paper>

          {/* Servers header row */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Available Servers ({entities.length})
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <TextField
                size="small"
                placeholder="Filter servers..."
                value={filter}
                onChange={e => setFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 220 }}
                inputProps={{ 'aria-label': 'Filter servers' }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() =>
                  navigate('/create/templates/default/mcp-server-hosting')
                }
              >
                Deploy New Server
              </Button>
            </Box>
          </Box>

          {/* Empty state */}
          {filteredEntities.length === 0 && entities.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              py={8}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No MCP Servers Found
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                Deploy your first MCP server using the "Deploy New Server"
                button.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
                gap: 3,
              }}
            >
              {filteredEntities.map(entity => {
                const isActive = entity.spec?.lifecycle === 'production';
                const teamName = getTeamName(entity);
                const docsUrl = getMcpDocs(entity);
                const entityUrl = getEntityUrl(entity);
                const color = getServerColor(entity.metadata.name);

                return (
                  <Card
                    key={entity.metadata.name}
                    variant="outlined"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <CardActionArea
                      onClick={() => navigate(entityUrl)}
                      sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                      }}
                    >
                      <CardContent
                        sx={{
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          p: 2.5,
                          '&:last-child': { pb: 2.5 },
                        }}
                      >
                        {/* Header: icon + active badge */}
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          mb={1.5}
                        >
                          <Avatar
                            sx={{
                              bgcolor: color,
                              borderRadius: '10px',
                              width: 44,
                              height: 44,
                              fontSize: 20,
                              fontWeight: 700,
                            }}
                          >
                            {getServerInitial(entity)}
                          </Avatar>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: isActive ? '#228189' : 'text.disabled',
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: isActive ? '#228189' : 'text.secondary',
                                fontWeight: 500,
                              }}
                            >
                              {isActive ? 'Active' : 'Inactive'}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Server name */}
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          mb={0.75}
                          sx={{ color: '#228189', lineHeight: 1.4 }}
                        >
                          {entity.metadata.title || entity.metadata.name}
                        </Typography>

                        {/* Description — clamped to 3 lines */}
                        <Typography
                          variant="body2"
                          mb={2}
                          sx={{
                            color: 'text.secondary',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.55,
                            flexGrow: 1,
                          }}
                        >
                          {entity.metadata.description ?? ''}
                        </Typography>

                        <Divider sx={{ mb: 1.5 }} />

                        {/* Bottom row: Add to Claude | owner | docs | eye */}
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1.5}
                          sx={{ flexWrap: 'nowrap', mt: 'auto' }}
                        >
                          {/* Add to Claude — first */}
                          <Box
                            component="button"
                            onClick={e => {
                              e.stopPropagation();
                              handleAddToClaude(entity);
                            }}
                            aria-label={`Add ${entity.metadata.name} to Claude`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              color: '#228189',
                              background: 'none',
                              border: 'none',
                              p: 0,
                              fontSize: '0.78rem',
                              fontFamily: 'inherit',
                              fontWeight: 600,
                              flexShrink: 0,
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 15 }} />
                            Add to Claude
                          </Box>

                          {/* Owner — centered */}
                          {teamName && (
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                              sx={{ minWidth: 0 }}
                            >
                              <GroupIcon
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: 15,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: '0.78rem',
                                  color: 'text.secondary',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {teamName}
                              </Typography>
                            </Box>
                          )}

                          {/* Docs */}
                          {docsUrl && (
                            <Link
                              href={docsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              onClick={e => e.stopPropagation()}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                flexShrink: 0,
                                fontSize: '0.78rem',
                              }}
                            >
                              <ArticleIcon sx={{ fontSize: 15 }} />
                              Docs
                            </Link>
                          )}

                          {/* Eye icon — view details */}
                          <Tooltip title="View details">
                            <IconButton
                              size="small"
                              onClick={e => {
                                e.stopPropagation();
                                navigate(entityUrl);
                              }}
                              sx={{ ml: 'auto', p: 0.25, flexShrink: 0 }}
                              aria-label={`View ${entity.metadata.name} details`}
                            >
                              <VisibilityIcon
                                sx={{ fontSize: 17, color: 'text.secondary' }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Box>
          )}

          <Snackbar
            open={copySnackbar}
            autoHideDuration={3000}
            onClose={() => setCopySnackbar(false)}
            message="Claude mcp add command copied to clipboard"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          />
        </Box>
      </Content>
    </Page>
  );
};
